import AsyncAutocompleteInput from '@/components/form/AsyncAutocompleteInput'
import { useFieldContext } from '@/lib/form-contexts'
import { useCustomers } from '@/lib/services/customers/customers.query'
import { cn } from '@/lib/utils'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import FieldInfo from './FieldInfo'

export default function CustomerSearch({
  id,
  label = 'Customer',
  placeholder = 'Customer name...',
  className,
  onCustomerSelected,
  allowAutofill = false,
}) {
  const field = useFieldContext()
  const [searchTerm, setSearchTerm] = useState('')

  // Get the display value - handle both string and object values
  const getDisplayValue = () => {
    const value = field.state.value
    if (typeof value === 'string') {
      return value
    }
    if (value && typeof value === 'object' && value.name) {
      return value.name
    }
    return ''
  }

  const { data: customers = [] } = useCustomers({
    filters: { search: searchTerm },
    enabled: !!(searchTerm && searchTerm.length >= 2),
    throwOnError: false,
  })

  const fetchCustomerOptions = useCallback(
    async (searchInput) => {
      setSearchTerm(searchInput)
      return (customers || []).map((customer) => ({
        id: customer.id,
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        value: customer.id,
      }))
    },
    [customers]
  )

  const handleOptionSelected = (option) => {
    try {
      // Find the full customer object
      const customer = customers.find((c) => c.id === option.value)
      field.handleChange(customer)
      onCustomerSelected?.(customer)
    } catch (error) {
      console.error('Error selecting customer:', error)
    }
  }

  const renderOption = useCallback((option) => {
    return (
      <div className="flex items-center gap-2">
        <span className="font-medium">{option.name}</span>
        <span className="text-sm text-muted-foreground">{option.email}</span>
        <span className="text-sm text-muted-foreground">{option.phone}</span>
      </div>
    )
  }, [])

  const onFetchError = useCallback(() => {
    toast.error('Error loading customer search', {
      description: 'Please enter the customer name manually or try again later.',
      duration: 5000,
    })
  }, [])

  return (
    <>
      <AsyncAutocompleteInput
        id={id}
        label={label}
        placeholder={placeholder}
        className={cn(className)}
        value={getDisplayValue()}
        onChange={() => {}}
        fetchOptions={fetchCustomerOptions}
        onOptionSelected={handleOptionSelected}
        allowAutofill={allowAutofill}
        minChars={3}
        onFetchError={onFetchError}
        renderOption={renderOption}
      />
      <FieldInfo />
    </>
  )
}
